import { Appointment, GeneralRequest, LoadedUser } from '../../interfaces';
import { AcceptRequestFlowValues } from '../../types/appointments';
import { initAcceptRequestFlowValues } from './acceptRequestFlow';
import { getArtistDefaultReply } from './artistDefaultReply';

export const hasFlashDesign = (
  request: Appointment | GeneralRequest | undefined,
): request is Appointment =>
  !!request && 'flashDesign' in request && !!request.flashDesign;

const getRequestServiceId = (
  request: Appointment | GeneralRequest | undefined,
) =>
  request && 'serviceId' in request && request.serviceId
    ? request.serviceId
    : undefined;

export const getRequestService = (
  request: Appointment | GeneralRequest | undefined,
  loadedUser: LoadedUser | null | undefined,
) => {
  if (hasFlashDesign(request)) return null;
  const serviceId = getRequestServiceId(request);
  if (!serviceId) return null;
  return (
    loadedUser?.business?.services?.find(s => s.serviceId === serviceId) ?? null
  );
};

export const getEmployeeHourlyRate = (
  loadedUser: LoadedUser | null | undefined,
  employeeId: string | undefined,
) =>
  employeeId
    ? loadedUser?.business?.employees?.find(e => e.employeeId === employeeId)
        ?.defaultHourlyRate
    : undefined;

// The one prefill implementation for accepting a request.
// Flash design or service owns the whole price block (a 0 deposit stays 0); hourly rate and default deposit apply only when there is neither.
export function getAcceptRequestInitialValues({
  request,
  loadedUser,
  cashDiscountEnabled,
}: {
  request: Appointment | GeneralRequest;
  loadedUser: LoadedUser;
  cashDiscountEnabled?: boolean;
}): AcceptRequestFlowValues {
  const isFlash = hasFlashDesign(request);
  const service = getRequestService(request, loadedUser);
  const serviceId = getRequestServiceId(request);
  const employeeId = 'employeeId' in request ? request.employeeId || '' : '';
  const hourlyRate =
    !isFlash && !service
      ? getEmployeeHourlyRate(loadedUser, employeeId)
      : undefined;
  const defaultTattooDeposit = loadedUser.business?.defaultTattooDeposit;
  const isConsultationRequest =
    'appointmentType' in request && request.appointmentType === 'CONSULTATION';

  const piercingLocationId =
    'piercingLocationId' in request && request.piercingLocationId
      ? String(request.piercingLocationId)
      : '';
  const locationCategory = piercingLocationId
    ? service?.piercingLocations?.find(
        location => String(location.locationId) === piercingLocationId,
      )?.locationCategory || ''
    : '';

  return {
    ...initAcceptRequestFlowValues,
    requestKind: isConsultationRequest ? 'CONSULTATION' : 'NEW_APPOINTMENT',
    // Trip requests have no business; never default one in or the accept
    // payload would attach the user's home shop to the trip
    businessId: request.businessId || '',
    employeeId,
    serviceId: serviceId ? String(serviceId) : '0',
    tattooLocation: request.tattooLocation || '',
    bodySide: request.bodySide || '',
    tattooSize: request.tattooSize || '',
    tattooColor: request.tattooColor || '',
    tattooDetails: request.tattooDetails || '',
    piercingLocationId,
    locationCategory,
    customDesignMessage: getArtistDefaultReply({
      loadedUser,
      employeeId,
    }),
    cashDiscountEnabled: cashDiscountEnabled || undefined,
    ...(!isFlash &&
      !service && {
        ...(defaultTattooDeposit && { depositPrice: defaultTattooDeposit }),
        ...(hourlyRate && { priceType: 'HOURLY', hourlyPrice: hourlyRate }),
      }),
    ...(service && {
      priceType: 'FIXED',
      price: service.defaultPrice || 0,
      depositPrice: service.defaultDepositPrice || 0,
      appointmentLengthInMinutes: service.defaultDurationInMinutes
        ? String(service.defaultDurationInMinutes)
        : '',
    }),
    ...(hasFlashDesign(request) && {
      priceType: 'FIXED',
      price: request.price || 0,
      depositPrice: request.depositPrice || 0,
      appointmentLengthInMinutes: request.appointmentLengthInMinutes
        ? String(request.appointmentLengthInMinutes)
        : '',
    }),
  };
}
